import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Form,
  InputGroup,
  ProgressBar,
  Spinner,
} from "react-bootstrap";
import {
  BsPlayBtn,
  BsPlayCircle,
  BsSearch,
} from "react-icons/bs";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../shared/styles/UtillitiesComponent.css";

import { auth, db } from "../../shared/libs/firebaseConfig";
import CourseAccessGate from "../../shared/components/CourseAccessGate";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import InstallmentCourseEntryGuard from "../../shared/components/InstallmentCourseEntryGuard";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function formatPercent(value) {
  const n = Number(value || 0);
  if (n >= 100) return 100;
  if (n <= 0) return 0;
  return Math.round(n);
}

function normalizeAllowedHeaderTypes(value) {
  if (!value) return null;
  if (!Array.isArray(value)) return null;

  const arr = value
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  return arr.length ? Array.from(new Set(arr)) : null;
}

function mergeAllowedHeaderTypesFromEnrolls(enrolls = []) {
  const merged = new Set();

  for (const e of enrolls) {
    const arr = normalizeAllowedHeaderTypes(e?.allowedHeaderTypes);
    if (!arr) continue;
    for (const v of arr) merged.add(v);
  }

  return merged.size ? Array.from(merged) : null;
}

function courseMatchesCategory(course, selectedCategory) {
  if (!selectedCategory || selectedCategory === "all") return true;

  const categories = Array.isArray(course?.categories) ? course.categories : [];
  const courseType = String(course?.courseType || "").trim();

  if (categories.includes(selectedCategory)) return true;

  const mapCourseTypeToCategory = {
    thaibar: "เนติบัณฑิต",
    attorney: "ทนายความ",
    prosecutor: "อัยการ",
    judge: "ผู้พิพากษา",
    ocsc: "สอบ ก.พ.",
  };

  return mapCourseTypeToCategory[courseType] === selectedCategory;
}

function courseMatchesStatus(course, statusFilter) {
  if (statusFilter === "all") return true;
  if (statusFilter === "active") return course?.isActive !== false;
  if (statusFilter === "inactive") return course?.isActive === false;
  return true;
}

async function fetchMyGrantedEnrollments(uid, maxFetch = 100) {
  const q1 = query(
    collection(db, "enrolls"),
    where("userId", "==", uid),
    where("accessStatus", "==", "granted"),
    orderBy("updatedAt", "desc"),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q1);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyGrantedEnrollments q1 failed:", e?.message);
  }

  const q2 = query(
    collection(db, "enrolls"),
    where("uid", "==", uid),
    where("accessStatus", "==", "granted"),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q2);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyGrantedEnrollments q2 failed:", e?.message);
  }

  return [];
}

async function fetchCourseProgress(uid, courseId) {
  try {
    const ref = doc(db, "users", uid, "courseProgress", courseId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.warn("fetchCourseProgress failed:", e?.message);
    return null;
  }
}

async function fetchCourseDoc(courseId) {
  try {
    const ref = doc(db, "courses", courseId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.warn("fetchCourseDoc failed:", e?.message);
    return null;
  }
}

/* async function fetchPublishedVideoLessonsWithDuration(courseId, allowedHeaderTypes = null) {
  const result = [];

  const allowedSet =
    Array.isArray(allowedHeaderTypes) && allowedHeaderTypes.length > 0
      ? new Set(allowedHeaderTypes.map((x) => String(x || "").trim()).filter(Boolean))
      : null;

  try {
    const headersSnap = await getDocs(
      query(collection(db, "courses", courseId, "headers"), orderBy("order", "asc"))
    );

    for (const headerDoc of headersSnap.docs) {
      const headerId = headerDoc.id;
      const header = headerDoc.data();
      const headerType = String(header?.headerType || "").trim();

      if (allowedSet && !allowedSet.has(headerType)) {
        continue;
      }

      const sectionsSnap = await getDocs(
        query(
          collection(db, "courses", courseId, "headers", headerId, "sections"),
          orderBy("order", "asc")
        )
      );

      for (const sectionDoc of sectionsSnap.docs) {
        const sectionId = sectionDoc.id;

        const itemsSnap = await getDocs(
          query(
            collection(
              db,
              "courses",
              courseId,
              "headers",
              headerId,
              "sections",
              sectionId,
              "items"
            ),
            orderBy("order", "asc")
          )
        );

        for (const itemDoc of itemsSnap.docs) {
          const item = itemDoc.data();
          const durationSec = Number(item?.video?.durationSec || 0);

          const isPublished = item?.contentStatus === "published";
          const isActive = item?.isActive !== false;
          const isVideoReady =
            item?.video?.isReady === true ||
            item?.video?.readyToStream === true ||
            item?.video?.status === "ready";

          if (isPublished && isActive && isVideoReady && durationSec > 0) {
            result.push({
              id: itemDoc.id,
              courseId,
              headerId,
              headerType,
              sectionId,
              title: item?.title || "",
              durationSec,
            });
          }
        }
      }
    }
  } catch (e) {
    console.warn(
      `fetchPublishedVideoLessonsWithDuration failed (${courseId}):`,
      e?.message
    );
  }

  return result;
} */

function buildProgressFromSummary(progress) {
  const percent = formatPercent(progress?.percent || 0);
  const totalDurationSec = Number(progress?.totalDurationSec || 0);
  const completedDurationSec = Number(progress?.completedDurationSec || 0);

  return {
    percent,
    percentLabel: percent,
    totalDurationSec,
    completedDurationSec,
    completedLessons: Number(progress?.completedLessons || 0),
    totalLessons: Number(progress?.totalLessons || 0),
    isComplete: percent >= 100 && totalDurationSec > 0,
  };
}

/* function computeCourseProgress(lessons, courseProgress) {
  const totalDurationSec = lessons.reduce((sum, x) => sum + Number(x.durationSec || 0), 0);

  const states = courseProgress?.lessonStates || {};
  let completedDurationSec = 0;

  for (const lesson of lessons) {
    const state = states?.[lesson.id];
    if (state?.completed === true) {
      completedDurationSec += Number(lesson.durationSec || 0);
    }
  }

  const rawPercent =
    totalDurationSec > 0 ? (completedDurationSec / totalDurationSec) * 100 : 0;

  const percent = clamp(rawPercent, 0, 100);
  const isComplete = percent >= 100 && totalDurationSec > 0;

  return {
    totalDurationSec,
    completedDurationSec,
    percent,
    percentLabel: formatPercent(percent),
    isComplete,
  };
} */

export default function MyCourseLists() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const [keyword, setKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const uid = auth?.currentUser?.uid;
        if (!uid) {
          if (!alive) return;
          setRows([]);
          return;
        }

        const enrolls = await fetchMyGrantedEnrollments(uid, 100);

        const enrollsByCourseId = enrolls.reduce((acc, e) => {
          const courseId = e?.courseId;
          if (!courseId) return acc;
          if (!acc[courseId]) acc[courseId] = [];
          acc[courseId].push(e);
          return acc;
        }, {});

        const courseIds = Object.keys(enrollsByCourseId);

        const data = await Promise.all(
          courseIds.map(async (courseId) => {
            const courseEnrolls = enrollsByCourseId[courseId] || [];
            const allowedHeaderTypes = mergeAllowedHeaderTypesFromEnrolls(courseEnrolls);

            const [course, progress] = await Promise.all([
              fetchCourseDoc(courseId),
              fetchCourseProgress(uid, courseId),
            ]);

            if (!course) return null;

            const computed = buildProgressFromSummary(progress);

            return {
              id: courseId,
              slug: course?.slug || courseId,
              title: course?.title || "คอร์สเรียน",
              subTitle: course?.subTitle || "",
              imageCoverUrl: course?.imageCoverUrl || "",
              categories: Array.isArray(course?.categories) ? course.categories : [],
              courseType: course?.courseType || "",
              isActive: course?.isActive !== false,
              updatedAt: course?.updatedAt || course?.createdAt || null,
              lastOpenedAt: progress?.lastOpenedAt || null,
              enrolls: courseEnrolls,
              allowedHeaderTypes,
              progress,
              ...computed,
            };
          })
        );

        const cleanRows = data.filter(Boolean);

        cleanRows.sort((a, b) => {
          const aLast = toDateSafe(a.lastOpenedAt)?.getTime() || 0;
          const bLast = toDateSafe(b.lastOpenedAt)?.getTime() || 0;
          if (aLast !== bLast) return bLast - aLast;

          const aUpd = toDateSafe(a.updatedAt)?.getTime() || 0;
          const bUpd = toDateSafe(b.updatedAt)?.getTime() || 0;
          return bUpd - aUpd;
        });

        if (!alive) return;
        setRows(cleanRows);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "โหลดคอร์สเรียนของฉันไม่สำเร็จ");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set(["all"]);

    for (const row of rows) {
      const cats = Array.isArray(row?.categories) ? row.categories : [];
      cats.forEach((c) => {
        const v = String(c || "").trim();
        if (v) set.add(v);
      });
    }

    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = keyword.trim().toLowerCase();

    return rows.filter((course) => {
      const hitKeyword =
        !q ||
        String(course?.title || "").toLowerCase().includes(q) ||
        String(course?.subTitle || "").toLowerCase().includes(q);

      return (
        hitKeyword &&
        courseMatchesCategory(course, selectedCategory) &&
        courseMatchesStatus(course, statusFilter)
      );
    });
  }, [rows, keyword, selectedCategory, statusFilter]);

  if (!auth?.currentUser) return null;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <BsPlayBtn />
        <h4 className="mb-0">คอร์สเรียนของฉัน</h4>
      </div>

      <Row className="g-2 mb-3">
        <Col xs={12} lg={6}>
          <InputGroup>
            <InputGroup.Text>
              <BsSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="ค้นหาชื่อคอร์ส"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>
                {cat === "all" ? "ทุกหมวดหมู่" : cat}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">เผยแพร่</option>
            <option value="inactive">หมดอายุ</option>
            <option value="all">ทั้งหมด</option>
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && error && (
        <div className="text-danger">{error}</div>
      )}

      {!loading && !error && filteredRows.length === 0 && (
        <div className="text-muted">ไม่พบคอร์สเรียนที่ตรงเงื่อนไข</div>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <div className="d-flex flex-column gap-3">
          {filteredRows.map((course) => {
            const percent = course.percentLabel;
            const isComplete = course.isComplete;

            return (
              <CourseAccessGate
                key={course.id}
                courseId={course.id}
                onAllowed={() => navigate(`/learn/${course.id}`)}
              >
                {({ openGate, checking }) => (
                  <InstallmentCourseEntryGuard
                    course={course}
                    onAllowed={openGate}
                  >
                    {({ handleEnter }) => (
                      <button
                        type="button"
                        onClick={handleEnter}
                        disabled={checking}
                        style={{
                          display: "block",
                          width: "100%",
                          padding: 0,
                          border: "none",
                          background: "transparent",
                          textAlign: "inherit",
                          color: "inherit",
                          cursor: checking ? "wait" : "pointer",
                        }}
                      >
                        <div className="card app-card rounded-4 overflow-hidden">
                          <Row className="g-0 align-items-stretch">
                            <Col xs={12} md={5} lg={4}>
                              <div
                                className="position-relative bg-surface w-100"
                                style={{
                                  aspectRatio: "16 / 9",
                                  overflow: "hidden",
                                }}
                              >
                                {course.imageCoverUrl ? (
                                  <>
                                    <img
                                      src={course.imageCoverUrl}
                                      alt={course.title}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />

                                    <div
                                      className="d-flex align-items-center justify-content-center"
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                          width: 70,
                                          height: 70,
                                          background: "rgba(0,0,0,0.32)",
                                          backdropFilter: "blur(2px)",
                                        }}
                                      >
                                        <BsPlayCircle
                                          size={54}
                                          style={{
                                            color: "#fff",
                                            opacity: 0.98,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center h-100 text-secondary"
                                    style={{ fontSize: 14 }}
                                  >
                                    ไม่มีรูปปก
                                  </div>
                                )}
                              </div>
                            </Col>

                            <Col xs={12} md={7} lg={8} style={{ minWidth: 0 }}>
                              <div className="card-body p-3 p-md-4">
                                <div className="d-flex flex-column gap-2 h-100">
                                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                    <div className="text-start" style={{ minWidth: 0 }}>
                                      <div
                                        className="fw-semibold text-main"
                                        style={{
                                          fontSize: 18,
                                          lineHeight: 1.4,
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {course.title}
                                      </div>

                                      {course.subTitle ? (
                                        <div
                                          className="text-secondary small mt-1"
                                          style={{ wordBreak: "break-word" }}
                                        >
                                          {course.subTitle}
                                        </div>
                                      ) : null}
                                    </div>

                                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                      {(course.categories || []).slice(0, 2).map((cat) => (
                                        <span key={cat} className="badge text-bg-light border">
                                          {cat}
                                        </span>
                                      ))}

                                      <span
                                        className={`badge ${
                                          course.isActive ? "text-bg-primary" : "text-bg-secondary"
                                        }`}
                                      >
                                        {course.isActive ? "เผยแพร่" : "หมดอายุ"}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="mt-1 text-start">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <small className="text-secondary">สถานะการเรียน</small>

                                      <small
                                        className={
                                          isComplete
                                            ? "text-success fw-semibold"
                                            : "text-primary fw-semibold"
                                        }
                                      >
                                        {isComplete ? "ครบถ้วน" : `ความก้าวหน้า ${percent}%`}
                                      </small>
                                    </div>

                                    <ProgressBar
                                      now={percent}
                                      variant={isComplete ? "success" : "primary"}
                                      style={{ height: 10, borderRadius: 999 }}
                                    />
                                  </div>

                                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-auto text-start">
                                    <small className="text-secondary">
                                      เปิดดูล่าสุด:{" "}
                                      {toDateSafe(course.lastOpenedAt)
                                        ? toDateSafe(course.lastOpenedAt).toLocaleString("th-TH")
                                        : "-"}
                                    </small>

                                    <small className="text-secondary">
                                      จำนวน EP: {course.totalLessons || 0}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </button>
                    )}
                  </InstallmentCourseEntryGuard>
                )}
              </CourseAccessGate>
            );
          })}
        </div>
      )}
    </div>
  );
}